// Client-side upload pre-processing: caps page weight and strips EXIF/GPS
// metadata (canvas re-encode drops it) before bytes ever leave the browser.
// The backend still sniffs and validates content — this is an optimization,
// not a security boundary.

// Must stay in sync with the server's normalize target
// (apps/backend/internal/shared/imageproc: FullMaxDim) — uploading more
// pixels than the server keeps just burns cellular upload time.
const MAX_DIMENSION = 2048;
const JPEG_QUALITY = 0.85;

export const downscaleImage = async (file: File, maxDim = MAX_DIMENSION): Promise<File> => {
  // Only re-encode raster photos; SVGs and unknowns pass through untouched.
  if (!file.type.startsWith("image/") || file.type === "image/svg+xml") {
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    // imageOrientation applies EXIF rotation before re-encoding, so the
    // stripped orientation tag can't break display.
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    return file; // undecodable here — let the server reject it
  }

  const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, width, height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );
  if (!blob) return file;

  const name = file.name.replace(/\.[^.]+$/, "") || "image";
  return new File([blob], `${name}.jpg`, { type: "image/jpeg" });
};
